// Enterprise brand directory: single source of truth for the /enterprise/ hub,
// the enquiry form's brand selector and the hub's ItemList schema.
//
// FOCUS LIST (spec docs/superpowers/specs/2026-10-01-enterprise-focus-brands-design.md).
// On 2026-10-01 the directory was cut from 69 brands to 14 (15 with Google Workspace, added 2026-10-03), where orders
// run to 10+ units, seats or subscriptions, or RM5,000+ a month (the soft minimum
// lives in order-minimum.ts). Add a brand only with Jazz's sign-off.
//
// WORDING RULES. The distributor is NEVER named. Claude is "we procure and manage
// Claude licences for you", never "resell". OpenAI seats are bought on the client's
// behalf and OpenAI models run via Azure OpenAI on an Azure subscription we provide;
// never "OpenAI reseller". Partner titles only as agreed: authorised reseller for
// Microsoft (GitHub Enterprise sits under Microsoft), Lenovo, NVIDIA DGX Spark
// (Jazz, 2026-10-04), Red Hat, Alibaba Cloud and TrueWatch (Jazz, 2026-10-03);
// Google Workspace reseller; Sonar reseller partner. NVIDIA data-centre GPUs are
// "supply" only; brand-conferred reseller titles only where
// `certified: true`. No logo for a brand new to the directory until Jazz
// confirms logo-usage rights.
//
// `published: true` means a dedicated /enterprise/{slug}/ page exists. `href`
// sends the card to a page outside /enterprise/ (Claude licences). Everything else
// links to the enquiry form pre-filled with the brand. The OpenAI card stays
// enquiry-only (the form is the quote path) and links the partner page through
// `alsoLink`, which owns the "OpenAI partner Malaysia" terms.

export type BrandCategory =
  | "AI & compute"
  | "Cloud & observability"
  | "Software licences"
  | "Hardware";

/** How a brand is bought: counted units, monthly usage, or either (seats or API). */
export type BrandBilling = "units" | "usage" | "both";

export interface EnterpriseBrand {
  name: string;
  slug: string; // /enterprise/{slug}/ when published; pattern {brand}-malaysia
  category: BrandCategory;
  published: boolean;
  /** One-line descriptor on the hub card; also an i18n key. No em dashes. */
  blurb: string;
  /** Filename in /public/images/brands/. Omit and the card shows a monogram. */
  logo?: string;
  certified?: boolean; // brand-conferred reseller certificate held
  /** Enquiry-form suggestions for brands without a template brand page. */
  products?: string[];
  /** Overrides the group-based default unit in the enquiry form. */
  unit?: string;
  /** Defaults to "units". */
  billing?: BrandBilling;
  /** Card target outside /enterprise/. Wins over `published`. */
  href?: string;
  /** A second, unlocalised link on the hub card (e.g. the Singapore page). */
  alsoLink?: { label: string; href: string };
}

/**
 * SonarQube editions and add-ons we quote. NO Server Data Center edition: we do
 * not supply it (Jazz, 2026-10-06); tests/sonarqube-content.spec.ts guards that.
 * Add-ons we resell on the editions/plans named in
 * docs/research/sonarqube-enterprise-push-2026-10-06.md (S5-S7, S19-S27).
 * AI CodeFix is not a line item: it is included in Server Enterprise and the
 * Cloud Team and Enterprise plans (S10).
 */
export const SONARQUBE_PRODUCTS = [
  "SonarQube Cloud Team",
  "SonarQube Cloud Enterprise",
  "SonarQube Server Developer Edition",
  "SonarQube Server Enterprise Edition",
  "SonarQube Advanced Security (add-on)",
  "SonarQube Remediation Agent (add-on)",
  "Sonar Vortex (add-on)",
  "SonarQube Hunter Agent (add-on)",
  "Gitar (AI pull request reviewer)",
  "SonarQube team training workshop",
  "Not sure yet: advise me",
];

export const enterpriseBrands: EnterpriseBrand[] = [
  // ── AI & compute ──
  { name: "NVIDIA DGX Spark", slug: "dgx-spark-malaysia", category: "AI & compute", published: true, blurb: "Authorised NVIDIA DGX Spark reseller: GB10 desktop AI supercomputers, every OEM build", unit: "units" },
  { name: "NVIDIA", slug: "nvidia-malaysia", category: "AI & compute", published: true, blurb: "GPUs for AI training & inference", logo: "nvidia.svg", unit: "units" },
  { name: "OpenAI", slug: "openai-malaysia", category: "AI & compute", published: false, blurb: "ChatGPT Business and Enterprise seats bought on your behalf; OpenAI models via Azure OpenAI", unit: "seats", billing: "both", products: ["ChatGPT Business seats", "ChatGPT Enterprise seats", "OpenAI models via Azure OpenAI", "Not sure yet: advise me"], alsoLink: { label: "OpenAI partner page", href: "/partners/openai-partner/" } },
  { name: "Claude licences", slug: "claude-licences-malaysia", category: "AI & compute", published: false, href: "/services/claude-managed-service-malaysia/", blurb: "We procure and manage Claude licences for you", unit: "seats", billing: "both", products: ["Claude Team seats", "Claude Enterprise seats", "Claude API usage", "Not sure yet: advise me"] },
  { name: "Alibaba Cloud Model Studio", slug: "alibaba-cloud-model-studio-malaysia", category: "AI & compute", published: false, blurb: "Authorised Alibaba Cloud reseller: Qwen and other models via API, billed monthly in RM", billing: "usage", products: ["Qwen models via Model Studio API", "Other Model Studio models", "Not sure yet: advise me"] },

  // ── Cloud & observability ──
  { name: "Alibaba Cloud", slug: "alibaba-cloud-malaysia", category: "Cloud & observability", published: false, blurb: "Authorised Alibaba Cloud reseller: compute, storage, database and other cloud products", billing: "usage", products: ["Compute", "Storage", "Databases", "Other Alibaba Cloud products", "Not sure yet: advise me"] },
  { name: "TrueWatch", slug: "truewatch-malaysia", category: "Cloud & observability", published: false, blurb: "Authorised TrueWatch reseller: observability for metrics, logs and traces", billing: "usage" },

  // ── Software licences ──
  { name: "Microsoft", slug: "microsoft-malaysia", category: "Software licences", published: true, blurb: "Authorised Microsoft reseller: Microsoft 365, Azure, Windows Server & Surface", unit: "seats" },
  { name: "GitHub Enterprise", slug: "github-malaysia", category: "Software licences", published: false, blurb: "Authorised Microsoft reseller: GitHub Enterprise, Copilot & Advanced Security", unit: "seats", products: ["GitHub Enterprise seats", "GitHub Copilot Business seats", "GitHub Copilot Enterprise seats", "GitHub Advanced Security", "Not sure yet: advise me"] },
  { name: "Red Hat", slug: "red-hat-malaysia", category: "Software licences", published: true, blurb: "Authorised Red Hat reseller: RHEL, OpenShift & Ansible subscriptions", logo: "red-hat.svg", unit: "subscriptions" },
  { name: "Adobe", slug: "adobe-malaysia", category: "Software licences", published: true, blurb: "Creative Cloud, Acrobat & VIP licensing", logo: "adobe.png", certified: true, unit: "seats" },
  { name: "SonarQube", slug: "sonarqube-malaysia", category: "Software licences", published: true, blurb: "Code quality & code security: licences, setup and training; SAST, AI CodeFix, Remediation Agent", unit: "licences", products: SONARQUBE_PRODUCTS, alsoLink: { label: "Buying in Singapore", href: "/singapore/sonarqube/" } },
  { name: "Google Workspace", slug: "google-workspace-malaysia", category: "Software licences", published: false, blurb: "Google Workspace reseller: licences, setup and migration", unit: "seats", products: ["Google Workspace Business Starter", "Google Workspace Business Standard", "Google Workspace Business Plus", "Google Workspace Enterprise", "Not sure yet: advise me"] },

  // ── Hardware ──
  { name: "Dell Technologies", slug: "dell-technologies-malaysia", category: "Hardware", published: true, blurb: "Authorised Dell reseller: PowerEdge servers, GPU compute & endpoints", logo: "dell-technologies.svg", certified: true, unit: "devices" },
  { name: "Lenovo", slug: "lenovo-malaysia", category: "Hardware", published: false, blurb: "Authorised Lenovo reseller: ThinkPad, ThinkSystem servers & workstations", logo: "lenovo.svg", unit: "devices" },
];

export const brandCategories: BrandCategory[] = [
  "AI & compute",
  "Cloud & observability",
  "Software licences",
  "Hardware",
];

export const publishedBrands = enterpriseBrands.filter((b) => b.published);

/** Where a hub card, related link or ItemList entry points. Unlocalised. */
export function brandHref(b: EnterpriseBrand): string {
  if (b.href) return b.href;
  return b.published ? `/enterprise/${b.slug}/` : `/enterprise/enquiry/?brand=${b.slug}`;
}

/** True when the card leads to a page with detail rather than the bare form. */
export function brandHasPage(b: EnterpriseBrand): boolean {
  return b.published || Boolean(b.href);
}

/**
 * Tile letters for brands we hold no logo asset for. Each brand takes the first
 * candidate not already claimed: initials for multi-word names, then
 * progressively longer prefixes, so two brands never share a tile.
 */
function monogramCandidates(name: string): string[] {
  const words = name.split(/[^A-Za-z0-9]+/).filter(Boolean);
  const flat = words.join("");
  const out: string[] = [];
  if (words.length > 1) out.push(words.map((w) => w[0]).join("").slice(0, 3));
  for (let n = 2; n <= 4; n += 1) out.push(flat.slice(0, n));
  return out.map((c) => c.toUpperCase());
}

function buildMonograms(brands: EnterpriseBrand[]): Record<string, string> {
  const taken = new Set<string>();
  const map: Record<string, string> = {};
  for (const b of brands) {
    const pick =
      monogramCandidates(b.name).find((c) => !taken.has(c)) ??
      b.slug.slice(0, 4).toUpperCase();
    taken.add(pick);
    map[b.slug] = pick;
  }
  return map;
}

/** Keyed by slug, so a brand's tile never changes when the list is reordered. */
export const brandMonograms = buildMonograms(enterpriseBrands);
